'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import type { Banner } from '@eliadmin/core/src/api/general/types/general-types';
import type { CarouselProps } from '@eliadmin/core/src/components/ui/carousel';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import type { AvailableLocales } from '@eliadmin/core/src/stores/locale-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { ViewportBannerImage } from '@eliadmin/core/src/components/image/viewport-banner-image';
import { BANNER_IMAGE_CONFIG } from '@eliadmin/core/src/constants/banner-image-config';
import { imageDimensions } from '@eliadmin/core/src/utils/image-dimensions';
import Link from '@eliadmin/core/src/components/link';

export type BannerSliderProps = {
  banners?: Banner[];
  locale?: AvailableLocales;

  className?: {
    main?: string;
    banner?: string;
  };

  carouselPropsMobile?: Omit<CarouselProps, 'slides'>;
  carouselPropsDesktop?: Omit<CarouselProps, 'slides'>;
};

export function BannerSlider(props: BannerSliderProps) {
  const currentLocale = useLocaleStore(s => s.currentLocale);
  const locale = props.locale ?? currentLocale;

  const { data: slides = [] } = useQuery({
    queryKey: [CacheKey.BANNERS, locale, { type: 'slider' }],
    queryFn: () =>
      fetchBanners({ locale, query: { where: { type: { equals: 'slider' } } } }).then(
        response => response.data,
      ),
    initialData: props.banners,
    staleTime: 60_000,
  });

  const { data: extraBanners } = useSuspenseQuery({
    queryKey: [CacheKey.BANNERS, locale, { type: 'banner' }],
    queryFn: () => fetchBanners({ locale, query: { where: { type: { equals: 'banner' } } } }),
    staleTime: 60_000,
  });

  if (!extraBanners.data) return null;

  const bannersSmall = extraBanners.data.slice(2).filter(item => item.image?.url);
  const dimensionsFor = (item: Banner) =>
    imageDimensions(item.image?.metadata) ??
    imageDimensions(BANNER_IMAGE_CONFIG.dimensions[item.image?.url ?? '']);

  const desktopSlides = slides
    .filter(i => i.types?.includes('desktop') && i.image)
    .map((item, i) => (
      <Link
        key={`desktop-slide-${item.id ?? item.image?.url ?? item.url ?? i}`}
        href={item.url ?? '#'}
        prefetch={false}
        className="block"
      >
        <ViewportBannerImage
          viewport="desktop"
          priority={i === 0}
          src={item.image.url}
          alt={resolveImageAlt(item?.image, item?.name)}
          width={dimensionsFor(item)?.width ?? 0}
          height={dimensionsFor(item)?.height ?? 0}
          quality={75}
          sizes="(min-width: 1280px) 810px, 65vw"
          className={cn(
            'h-auto min-h-[300px] w-full rounded-lg bg-primary/10 object-cover object-center',
            'ea-banner-slider__banner',
            'ea-banner-slider__banner--desktop',
            props.className?.banner,
          )}
        />
      </Link>
    ));

  const mobileSlides = slides
    .filter(i => i.types?.includes('mobile') && i.image)
    .map((item, i) => (
      <div
        key={`mobile-slide-${item.id ?? item.image?.url ?? item.url ?? i}`}
        className="h-[370px] py-2"
      >
        <Link href={item.url ?? '#'} prefetch={false}>
          <ViewportBannerImage
            viewport="mobile"
            src={item.image.url}
            alt={resolveImageAlt(item?.image, item?.name)}
            priority={i === 0}
            width={dimensionsFor(item)?.width ?? 0}
            height={dimensionsFor(item)?.height ?? 0}
            quality={75}
            sizes="100vw"
            className={cn(
              'h-[370px] w-full rounded-[32px] bg-primary/10 object-cover object-center px-2 md:min-h-[300px]',
              'ea-banner-slider__banner',
              'ea-banner-slider__banner--mobile',
              props.className?.banner,
            )}
          />
        </Link>
      </div>
    ));

  if (!desktopSlides.length && !mobileSlides.length) return null;

  return (
    <>
      {/* desktop */}
      <div className="hidden min-w-0 grid-cols-[minmax(0,65fr)_minmax(0,35fr)] gap-3 md:grid">
        {/* sldier */}
        <div
          className={cn(
            'flex w-full min-w-0 rounded-lg',
            'ea-banner-slider',
            'ea-banner-slider__desktop',
            props.className?.main,
          )}
        >
          <Carousel
            {...props.carouselPropsDesktop}
            slides={desktopSlides}
            enableLoop
            enableInterval
          />
        </div>
        {/* banners */}
        <div className="flex w-full min-w-0 flex-col gap-2">
          {bannersSmall.map((item, i) => {
            return (
              <Link
                href={item.url ?? '#'}
                prefetch={false}
                className="block"
                key={`desktop-banner-${item.id ?? item.image?.url ?? item.url ?? i}`}
              >
                <ViewportBannerImage
                  viewport="desktop"
                  alt={resolveImageAlt(item?.image, item?.name)}
                  width={dimensionsFor(item)?.width ?? 0}
                  height={dimensionsFor(item)?.height ?? 0}
                  sizes="(min-width: 1280px) 440px, 35vw"
                  className="h-auto w-full rounded-lg bg-primary/10"
                  src={item.image.url}
                />
              </Link>
            );
          })}
        </div>
      </div>

      {/* mobile */}
      <div className="flex flex-col gap-3 md:hidden">
        <div
          className={cn(
            'flex h-[400px] w-full max-w-screen-xl',
            'ea-banner-slider',
            'ea-banner-slider__mobile',
            props.className?.main,
          )}
        >
          <Carousel
            {...props.carouselPropsMobile}
            slides={mobileSlides}
            showDots
            enableLoop
            className={{
              ...props.carouselPropsMobile?.className,
              slide: cn('!w-[95vw] sm:!w-full', props.carouselPropsMobile?.className?.slide),
            }}
          />
        </div>
        {/* banners */}
        <div className="flex w-full flex-col gap-3 px-2 md:hidden">
          {bannersSmall.map((item, i) => {
            return (
              <Link
                href={item.url ?? '#'}
                prefetch={false}
                className="block"
                key={`mobile-banner-${item.id ?? item.image?.url ?? item.url ?? i}`}
              >
                <ViewportBannerImage
                  viewport="mobile"
                  alt={resolveImageAlt(item?.image, item?.name)}
                  width={dimensionsFor(item)?.width ?? 0}
                  height={dimensionsFor(item)?.height ?? 0}
                  sizes="100vw"
                  className="h-auto w-full rounded-lg bg-primary/10"
                  src={item.image.url}
                />
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
